import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TagContract } from '@app-types/api/tag';
import {
  ContactEmail,
  ContactGetRequest,
  ContactInfo,
} from '@app-types/api/contact';
import { ContactType, ContractEmailType } from '@app-types/enums/contacts';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { DrawerService } from '@app-services/drawer.service';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingButtonComponent } from '@app-components/common/loading-button/loading-button.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SomethingWentWrongComponent } from '@app-components/common/error/something-went-wrong/something-went-wrong.component';
import { SkeletonFormDrawerComponent } from '@app-components/common/skeletons/skeleton-form-drawer/skeleton-form-drawer.component';
import { FieldTagsListComponent } from '@app-components/common/field-tags-list/field-tags-list.component';
import { SidePanelShellComponent } from '@app-components/common/side-panel-shell/side-panel-shell.component';
import { AddTagsDialogComponent } from '@app-components/common/add-tags-dialog/add-tags-dialog.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';
import { Subscription } from 'rxjs';
import { ContactClient } from '@app-services/api/clients/contact.client';
import { SnackbarService } from '@app-services/snackbar.service';
import {
  duplicateContactEmailValidator,
  italianTaxCodeValidator,
  italianVatNumberValidator,
} from '@app-utils/custom-validator';

const emptyStringFields = {
  type: false,
  alias: '',
  companyName: '',
  firstName: '',
  lastName: '',
  tags: [] as TagContract[],
  vatNumber: '',
  vatCountry: '',
  eoriCode: '',
  taxCode: '',
  transmissionFormat: '',
  street: '',
  city: '',
  address: '',
  postalCode: '',
  region: '',
  country: '',
  recipientCode: '',
  routingCode: '',
} as const;

@Component({
  selector: 'app-contact-dialog',
  templateUrl: './contact-dialog.component.html',
  styleUrls: ['./contact-dialog.component.scss'],
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    LoadingButtonComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    SomethingWentWrongComponent,
    SkeletonFormDrawerComponent,
    FieldTagsListComponent,
    SidePanelShellComponent,
    AddTagsDialogComponent,
    MatIconModule,
    MatTooltip,
    MatRadioModule,
  ],
})
export class ContactDialogComponent implements OnInit, OnDestroy {
  @Input() public contactId: number | null = null;
  @Input() public mailAccountId: number | null = null;
  @Input() public useMobileView = false;
  @Input({ required: true })
  public loadTags!: (search?: string | null) => Promise<TagContract[]>;
  @Input({ required: true })
  public insertTag!: (tag: string, color: string) => Promise<TagContract>;
  @Output() public sendEmailToContact = new EventEmitter<ContactInfo[]>();
  @Output() public submitEventEmitter = new EventEmitter<void>();
  @Output() public unsavedStateChange = new EventEmitter<boolean>();

  public isLoading = false;
  public saving = false;
  public savingAndNew = false;
  public cannotLoadData = false;
  public tagMenuIsOpened = false;

  public form = this.createEmptyForm();

  private typeChangeSub?: Subscription;
  private dirtyStateSub?: Subscription;
  private initContactInfo: ContactInfo | null = null;

  constructor(
    public matchError: MatchError,
    private drawerService: DrawerService,
    private contactClient: ContactClient,
    private snackbarService: SnackbarService
  ) {}

  get isEditMode(): boolean {
    return !!this.contactId;
  }

  ngOnInit(): void {
    this.contactId && this.loadData(this.contactId);
    this.syncCompanyNameRequired();
    this.syncNaturalPersonNameRequired();
    this.syncTaxCodeValidator();
    this.syncEmailAddressValidators();
    this.typeChangeSub = this.form
      .get('type')
      ?.valueChanges.subscribe((isNatural: boolean | null) => {
        this.clearFieldsForContactType(!!isNatural);
        this.syncCompanyNameRequired();
        this.syncNaturalPersonNameRequired();
        this.syncTaxCodeValidator();
      });
    this.dirtyStateSub = this.form.valueChanges.subscribe(() => {
      this.revalidateAllContactEmails();
      this.emitUnsavedState();
    });
    this.emitUnsavedState();
  }

  async loadData(contactId: number): Promise<void> {
    this.isLoading = true;
    this.cannotLoadData = false;
    try {
      const response = await this.contactClient.getById(
        new ContactGetRequest(contactId, this.mailAccountId)
      );
      const {
        emails,
        mailAccountId,
        contactId: id,
        type,
        ...rest
      } = response.result;
      this.form.patchValue({
        ...rest,
        type: type === ContactType.Person,
      });
      this.applyContactEmailsToFormArrays(response.result);
      this.initContactInfo = response.result;
    } catch (e) {
      this.matchError.logError(e);
      this.cannotLoadData = true;
    } finally {
      this.isLoading = false;
      this.form.markAsPristine();
      if (!this.cannotLoadData) {
        this.syncCompanyNameRequired();
        this.syncNaturalPersonNameRequired();
        this.syncTaxCodeValidator();
      }
      this.emitUnsavedState();
    }
  }

  public hasError(controlName: string, errorName: string): boolean {
    return this.form.get(controlName)?.hasError(errorName) ?? false;
  }

  public getEmailErrorMessage(control: AbstractControl): string {
    const errors = (control as FormGroup).get('email')?.errors;
    if (!errors) {
      return '';
    }

    if (errors['required']) {
      return 'fieldRequired';
    }
    if (errors['email']) {
      return 'incorrectEmail';
    }
    if (errors['duplicateEmail']) {
      return 'duplicateEmail';
    }

    return '';
  }

  public isLastPecLine(control: AbstractControl): boolean {
    const a = this.pecAddresses;
    return a.length > 0 && a.at(a.length - 1) === control;
  }

  public isLastEmailLine(control: AbstractControl): boolean {
    const a = this.emailAddresses;
    return a.length > 0 && a.at(a.length - 1) === control;
  }

  public removePecLine(control: AbstractControl): void {
    const i = this.pecAddresses.controls.indexOf(control);
    if (i < 0) {
      return;
    }
    this.removePecAddress(i);
  }

  public removeEmailLine(control: AbstractControl): void {
    const i = this.emailAddresses.controls.indexOf(control);
    if (i < 0) {
      return;
    }
    this.removeEmailAddress(i);
  }

  public onDefaultEmailChanged(
    listName: 'pecAddresses' | 'emailAddresses',
    controlOrIndex: AbstractControl | number
  ): void {
    const arr = this.form.get(listName) as FormArray;
    const index =
      typeof controlOrIndex === 'number'
        ? controlOrIndex
        : arr.controls.indexOf(controlOrIndex);
    if (index < 0) {
      return;
    }
    this.setDefaultEmail(listName, index);
  }

  public async submit(saveAndCreateNew = false): Promise<void> {
    if (!this.canSubmit) {
      this.form.markAllAsTouched();
      return;
    }

    this.savingAndNew = saveAndCreateNew;
    this.saving = !saveAndCreateNew;
    this.drawerService.disabledDrawer(true);

    try {
      const { type, pecAddresses, emailAddresses, ...rest } = this.form.value;
      const emailAddressesInfo = this.emailAddresses.controls.map(
        control => (control as FormGroup).getRawValue() as ContactEmail
      );
      const pecAddressesInfo = this.pecAddresses.controls.map(
        control => (control as FormGroup).getRawValue() as ContactEmail
      );
      const normalizedEmails = [...emailAddressesInfo, ...pecAddressesInfo]
        .filter(email => !!email.email?.trim())
        .map(email => {
          const normalized = {
            ...email,
            email: email.email.trim(),
          };
          if (normalized.contactEmailId) {
            return normalized;
          }
          return {
            email: normalized.email,
            type: normalized.type,
            isDefault: normalized.isDefault,
          };
        });

      const newData = Object.fromEntries(
        Object.entries(rest).map(([key, value]) => {
          if (value === null || value === undefined) {
            return [key, null];
          }
          if (typeof value === 'string') {
            const t = value.trim();
            return [key, t.length ? t : null];
          }
          return [key, value];
        })
      );

      const data: ContactInfo = {
        mailAccountId: this.mailAccountId,
        type: type ? ContactType.Person : ContactType.Company,
        ...newData,
        emails: normalizedEmails,
      };

      this.contactId
        ? await this.contactClient.update({
            contactId: this.contactId,
            ...data,
          })
        : await this.contactClient.create({
            contactId: this.contactId,
            ...data,
          });

      this.drawerService.disabledDrawer(false);
      this.submitEventEmitter.emit();
      if (saveAndCreateNew && !this.isEditMode) {
        this.resetForNextContact();
        this.snackbarService.openSnackBar('contactSaving');
      } else {
        this.drawerService.closeDrawer();
      }
    } catch (e) {
      this.drawerService.disabledDrawer(false);
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.saving = false;
      this.savingAndNew = false;
    }
  }

  protected resetForNextContact(): void {
    this.form.reset({ ...emptyStringFields });
    this.resetPecAndEmailFormArrays();
    this.syncCompanyNameRequired();
    this.syncNaturalPersonNameRequired();
    this.syncTaxCodeValidator();
    this.emitUnsavedState();
  }

  get pecAddresses(): FormArray {
    return this.form.get('pecAddresses') as FormArray;
  }

  get emailAddresses(): FormArray {
    return this.form.get('emailAddresses') as FormArray;
  }

  addPecAddress(): void {
    this.pecAddresses.push(
      this.createEmailFormGroup({
        email: '',
        type: ContractEmailType.PEC,
        isDefault: false,
      })
    );
    this.syncEmailAddressValidators();
  }

  addEmailAddress(): void {
    this.emailAddresses.push(
      this.createEmailFormGroup({
        email: '',
        type: ContractEmailType.Email,
        isDefault: false,
      })
    );
    this.syncEmailAddressValidators();
  }

  removePecAddress(index: number): void {
    if (this.pecAddresses.length < 2) {
      return;
    }
    this.pecAddresses.removeAt(index);
    this.ensureOneDefaultEmail();
  }

  removeEmailAddress(index: number): void {
    if (this.emailAddresses.length < 2) {
      return;
    }
    this.emailAddresses.removeAt(index);
    this.ensureOneDefaultEmail();
  }

  private createEmptyForm(): FormGroup {
    return new FormGroup({
      type: new FormControl(false),
      alias: new FormControl('', [
        Validators.required,
        Validators.maxLength(256),
      ]),
      companyName: new FormControl('', [Validators.maxLength(256)]),
      firstName: new FormControl('', [Validators.maxLength(256)]),
      lastName: new FormControl('', [Validators.maxLength(256)]),
      tags: new FormControl<TagContract[]>([]),
      vatNumber: new FormControl('', [
        Validators.maxLength(11),
        italianVatNumberValidator(),
      ]),
      vatCountry: new FormControl('', [Validators.maxLength(64)]),
      eoriCode: new FormControl('', [Validators.maxLength(64)]),
      taxCode: new FormControl('', [Validators.maxLength(16)]),
      city: new FormControl('', [Validators.maxLength(128)]),
      postalCode: new FormControl('', [Validators.maxLength(32)]),
      country: new FormControl('', [Validators.maxLength(128)]),
      pecAddresses: this.createPecFormArray(),
      emailAddresses: this.createEmailFormArray(),
      street: new FormControl('', [Validators.maxLength(256)]),
      address: new FormControl('', [Validators.maxLength(256)]),
      transmissionFormat: new FormControl('', [Validators.maxLength(64)]),
      recipientCode: new FormControl('', [Validators.maxLength(64)]),
      routingCode: new FormControl('', [Validators.maxLength(64)]),
      region: new FormControl('', [Validators.maxLength(128)]),
    });
  }

  private createPecFormArray(): FormArray {
    return new FormArray([
      this.createEmailFormGroup({
        email: '',
        type: ContractEmailType.PEC,
        isDefault: true,
      }),
    ]);
  }

  private createEmailFormArray(): FormArray {
    return new FormArray([
      this.createEmailFormGroup({
        email: '',
        type: ContractEmailType.Email,
        isDefault: false,
      }),
    ]);
  }

  private createEmailFormGroup(value: ContactEmail): FormGroup {
    const emailControl = new FormControl(value.email, {
      validators: this.emailValidatorsForDefaultRow(value.isDefault),
    });
    return new FormGroup({
      contactEmailId: new FormControl(value.contactEmailId ?? null),
      contactId: new FormControl(value.contactId ?? null),
      email: emailControl,
      type: new FormControl(value.type),
      isDefault: new FormControl(value.isDefault),
      createdDateTime: new FormControl(value.createdDateTime ?? null),
    });
  }

  private emailValidatorsForDefaultRow(isDefault: boolean): ValidatorFn[] {
    if (isDefault) {
      return [Validators.required, Validators.maxLength(256), Validators.email];
    }
    return [Validators.maxLength(256), Validators.email];
  }

  private syncEmailAddressValidators(): void {
    const apply = (arr: FormArray) => {
      arr.controls.forEach(c => {
        const group = c as FormGroup;
        const emailCtrl = group.get('email') as FormControl<string | null>;
        const isDefault = !!group.get('isDefault')?.value;
        emailCtrl.setValidators([
          ...this.emailValidatorsForDefaultRow(isDefault),
          duplicateContactEmailValidator(() => this.getAllContactEmailValues()),
        ]);
        emailCtrl.updateValueAndValidity({ emitEvent: false });
      });
    };
    apply(this.pecAddresses);
    apply(this.emailAddresses);
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private getAllContactEmailValues(): (string | null | undefined)[] {
    const values: (string | null | undefined)[] = [];
    const collect = (arr: FormArray) => {
      arr.controls.forEach(control => {
        values.push((control as FormGroup).get('email')?.value);
      });
    };
    collect(this.pecAddresses);
    collect(this.emailAddresses);
    return values;
  }

  private revalidateAllContactEmails(): void {
    const revalidate = (arr: FormArray) => {
      arr.controls.forEach(control => {
        (control as FormGroup).get('email')?.updateValueAndValidity({
          emitEvent: false,
        });
      });
    };
    revalidate(this.pecAddresses);
    revalidate(this.emailAddresses);
  }

  private resetPecAndEmailFormArrays(): void {
    this.form.setControl('pecAddresses', this.createPecFormArray());
    this.form.setControl('emailAddresses', this.createEmailFormArray());
    this.ensureOneDefaultEmail();
  }

  private getContactPrimaryEmail(contact: ContactInfo): string | null {
    const row = contact.emails?.find(e => e.isDefault);
    const raw = `${row?.email ?? ''}`.trim();
    return raw.length ? raw : null;
  }

  onSendEmailToContactClick(): void {
    if (!this.contactId || !this.initContactInfo) {
      return;
    }
    this.sendEmailToContact.emit([this.initContactInfo]);
  }

  private resolveDefaultEmailFlags(
    contact: ContactInfo,
    pecData: ContactEmail[],
    mailData: ContactEmail[]
  ): void {
    const combined: ContactEmail[] = [...pecData, ...mailData];
    const primary = this.getContactPrimaryEmail(contact);

    const setAllFalse = (): void => {
      combined.forEach(e => {
        e.isDefault = false;
      });
    };

    const flagged = combined.filter(e => e.isDefault);
    const byPrimary = primary
      ? combined.find(
          e => `${e.email ?? ''}`.trim().toLowerCase() === primary.toLowerCase()
        )
      : undefined;

    if (flagged.length === 1) {
      setAllFalse();
      flagged[0].isDefault = true;
      return;
    }

    if (flagged.length > 1) {
      setAllFalse();
      const pick =
        byPrimary && flagged.includes(byPrimary) ? byPrimary : flagged[0];
      pick.isDefault = true;
      return;
    }

    setAllFalse();
    if (byPrimary) {
      byPrimary.isDefault = true;
      return;
    }

    const firstNonEmpty = combined.find(e => !!`${e.email ?? ''}`.trim());
    if (firstNonEmpty) {
      firstNonEmpty.isDefault = true;
      return;
    }

    if (pecData.length) {
      pecData[0].isDefault = true;
    } else if (mailData.length) {
      mailData[0].isDefault = true;
    }
  }

  private applyContactEmailsToFormArrays(contact: ContactInfo): void {
    const pecs =
      contact.emails?.filter(e => e.type === ContractEmailType.PEC) ?? [];
    const mails =
      contact.emails?.filter(e => e.type === ContractEmailType.Email) ?? [];

    const pecData: ContactEmail[] = pecs.map(e => ({
      ...e,
      isDefault: e.isDefault,
    }));
    const mailData: ContactEmail[] = mails.map(e => ({
      ...e,
      isDefault: e.isDefault,
    }));

    this.resolveDefaultEmailFlags(contact, pecData, mailData);

    this.form.setControl(
      'pecAddresses',
      new FormArray(
        (pecData.length
          ? pecData
          : [
              {
                email: '',
                type: ContractEmailType.PEC,
                isDefault: false,
              },
            ]
        ).map(v =>
          this.createEmailFormGroup({
            ...v,
            type: ContractEmailType.PEC,
            isDefault: v.isDefault,
          })
        )
      )
    );
    this.form.setControl(
      'emailAddresses',
      new FormArray(
        (mailData.length
          ? mailData
          : [
              {
                email: '',
                type: ContractEmailType.Email,
                isDefault: false,
              },
            ]
        ).map(v =>
          this.createEmailFormGroup({
            ...v,
            type: ContractEmailType.Email,
            isDefault: v.isDefault,
          })
        )
      )
    );
    this.ensureOneDefaultEmail();
  }

  private setDefaultEmail(
    listName: 'pecAddresses' | 'emailAddresses',
    index: number
  ): void {
    const pecs = this.pecAddresses.controls;
    const emails = this.emailAddresses.controls;

    pecs.forEach(control =>
      (control as FormGroup)
        .get('isDefault')
        ?.setValue(false, { emitEvent: false })
    );
    emails.forEach(control =>
      (control as FormGroup)
        .get('isDefault')
        ?.setValue(false, { emitEvent: false })
    );

    const targetArray =
      listName === 'pecAddresses' ? this.pecAddresses : this.emailAddresses;
    (targetArray.at(index) as FormGroup)
      .get('isDefault')
      ?.setValue(true, { emitEvent: false });
    this.syncEmailAddressValidators();
  }

  private ensureOneDefaultEmail(): void {
    const pecs = this.pecAddresses.controls as FormGroup[];
    const emails = this.emailAddresses.controls as FormGroup[];
    const all = [...pecs, ...emails];

    const selectedDefault = all.find(
      control => !!control.get('isDefault')?.value
    );
    if (selectedDefault) {
      all.forEach(control =>
        control
          .get('isDefault')
          ?.setValue(control === selectedDefault, { emitEvent: false })
      );
      this.syncEmailAddressValidators();
      return;
    }

    if (pecs.length) {
      pecs[0].get('isDefault')?.setValue(true, { emitEvent: false });
      this.syncEmailAddressValidators();
      return;
    }
    if (emails.length) {
      emails[0].get('isDefault')?.setValue(true, { emitEvent: false });
    }
    this.syncEmailAddressValidators();
  }

  get canSubmit(): boolean {
    return this.form.valid && this.hasSelectedDefaultWithEmail();
  }

  private hasSelectedDefaultWithEmail(): boolean {
    const all = [
      ...(this.pecAddresses.controls as FormGroup[]),
      ...(this.emailAddresses.controls as FormGroup[]),
    ];
    return all.some(control => {
      const isDefault = !!control.get('isDefault')?.value;
      const email = `${control.get('email')?.value ?? ''}`.trim();
      return isDefault && !!email;
    });
  }

  get currentTags(): TagContract[] {
    const tags = this.form.get('tags')?.value as TagContract[] | undefined;
    return [...(tags ?? [])];
  }

  openAddTag(): void {
    this.tagMenuIsOpened = true;
  }

  closeAddTag(): void {
    this.tagMenuIsOpened = false;
  }

  saveLocalTags = async (selectedTags: TagContract[]): Promise<void> => {
    this.form.patchValue({ tags: selectedTags });
    this.tagMenuIsOpened = false;
  };

  ngOnDestroy(): void {
    this.typeChangeSub?.unsubscribe();
    this.dirtyStateSub?.unsubscribe();
  }

  private emitUnsavedState(): void {
    this.unsavedStateChange.emit(this.form.dirty);
  }

  private syncCompanyNameRequired(): void {
    const isNatural = !!this.form.get('type')?.value;
    const company = this.form.get('companyName');
    if (!company) {
      return;
    }
    if (isNatural) {
      company.setValidators([Validators.maxLength(256)]);
    } else {
      company.setValidators([Validators.required, Validators.maxLength(256)]);
    }
    company.updateValueAndValidity({ emitEvent: false });
  }

  private syncNaturalPersonNameRequired(): void {
    const isNatural = !!this.form.get('type')?.value;
    const firstName = this.form.get('firstName');
    if (!firstName) {
      return;
    }
    if (isNatural) {
      firstName.setValidators([Validators.required, Validators.maxLength(256)]);
    } else {
      firstName.setValidators([Validators.maxLength(256)]);
    }
    firstName.updateValueAndValidity({ emitEvent: false });
  }

  private syncTaxCodeValidator(): void {
    const isNatural = !!this.form.get('type')?.value;
    const taxCode = this.form.get('taxCode');
    if (!taxCode) {
      return;
    }
    if (isNatural) {
      taxCode.setValidators([
        Validators.maxLength(16),
        italianTaxCodeValidator(),
      ]);
    } else {
      taxCode.setValidators([Validators.maxLength(16)]);
    }
    taxCode.updateValueAndValidity({ emitEvent: false });
  }

  private clearFieldsForContactType(isNatural: boolean): void {
    if (isNatural) {
      this.form.patchValue({ companyName: '' }, { emitEvent: false });
    } else {
      this.form.patchValue(
        { firstName: '', lastName: '' },
        { emitEvent: false }
      );
    }
  }
}
